import clsx from 'clsx'
import Image from 'next/image'
import { ENTITY, WEBSITE, BTCtoSAT } from 'src/consts'

import {
  formatCurrency2String,
  formatValueWithCurrency,
  switchCurrency,
  switchPriceBtc,
} from 'src/utils/number/currency'
import { formatChangeValue } from 'src/utils/number/number'

import { Icon } from 'src/components/Icon'
import Colors from 'src/components/TreeMap/components/Colors'
import styles from './styles.module.scss'
import { intl } from 'src/i18n'

const colors = new Colors().createScale()

const SymbolPriceChange = ({ change }) => {
  const percentPriceChange = formatChangeValue(change)

  return (
    <span
      className={styles.change}
      style={{
        color: colors(Math.round(change * 100) / 100),
      }}
    >
      {percentPriceChange ? `(${percentPriceChange}%)` : '?'}
    </span>
  )
}

export const Widget = ({
  coinData,
  primaryCurrency,
  primaryTickerSymbol,
  secondaryCurrency,
  showRank,
  showMarketCap,
  showVolume,
  brightVersion,
}) => {
  const {
    name,
    slug,
    code,
    priceBtc,
    price,
    change,
    marketCapRank,
    marketCapBtc,
    marketCap,
    volumeBtc,
    volume,
  } = coinData

  const finalPrimaryPrice = primaryCurrency === 'BTC' ? priceBtc : price
  const satPrice = priceBtc * BTCtoSAT
  const safePriceBTC =
    priceBtc < 1e-6
      ? switchPriceBtc('SAT', {
          priceBtc:
            typeof satPrice === 'number' ? satPrice.toFixed(4) : satPrice,
        })
      : switchPriceBtc('BTC', { priceBtc })
  const finalSecondaryPrice = {
    USD: `${switchCurrency('USD', { price })} ${secondaryCurrency}`,
    BTC: safePriceBTC,
  }[secondaryCurrency]
  const finalMarketCap = primaryCurrency === 'BTC' ? marketCapBtc : marketCap
  const finalVolume = primaryCurrency === 'BTC' ? volumeBtc : volume

  return (
    <div className={clsx(styles.widget, { [styles.bright]: brightVersion })}>
      <a
        className={clsx(styles.row, styles.top)}
        // TODO additional params
        href={`${WEBSITE}/coin/${slug}`}
        target='_blank'
        rel='noopener noreferrer'
      >
        <Icon
          data={{ slug, name }}
          size='48x48'
          entity={ENTITY.COIN}
          className={styles.icon}
        />
        <div className={styles.data}>
          <div className={clsx(styles.dataitem, styles.name)}>
            {name}
            {primaryTickerSymbol && (
              <span className={styles.symbol}>({code})</span>
            )}
          </div>
          <div className={clsx(styles.dataitem, styles.price)}>
            {formatCurrency2String(finalPrimaryPrice)} {primaryCurrency}
            <SymbolPriceChange change={change} />
          </div>
          {secondaryCurrency !== 'None' && (
            <div className={clsx(styles.dataitem, styles.secondary)}>
              {finalSecondaryPrice}
            </div>
          )}
        </div>
      </a>
      {(showRank || showMarketCap || showVolume) && (
        <a
          className={clsx(styles.row, styles.middle)} // TODO additional params
          href={`${WEBSITE}/coin/${slug}`}
          target='_blank'
          rel='noopener noreferrer'
        >
          {showRank && (
            <div className={styles.column}>
              <div className={styles.head}>
                {intl.t('components.widget.rank')}
              </div>
              <div className={styles.desc}>#{marketCapRank}</div>
            </div>
          )}
          {showMarketCap && (
            <div className={styles.column}>
              <div className={styles.head}>
                {intl.t('components.widget.marketCap')}
              </div>
              <div className={styles.desc}>
                {formatValueWithCurrency(finalMarketCap, '', false, true)}
              </div>
            </div>
          )}
          {showVolume && (
            <div className={styles.column}>
              <div className={styles.head}>
                {intl.t('components.widget.volume')}
              </div>
              <div className={styles.desc}>
                {formatValueWithCurrency(finalVolume, '', false, true)}
              </div>
            </div>
          )}
        </a>
      )}
      <a
        className={clsx(styles.row, styles.bottom)}
        href={`${WEBSITE}/coin/${slug}`}
        target='_blank'
        rel='noopener noreferrer'
      >
        {intl.t('components.widget.poweredBy')}
        <div className={styles.img}>
          <Image
            src={`/assets/logo_${brightVersion ? 'dark' : 'gray'}.svg`}
            alt='Cryptocurrency prices in USD, market cap, volume'
            width='90'
            height='12'
          />
        </div>
      </a>
    </div>
  )
}

export default Widget
